import { computed } from "vue";
import { defineStore } from "pinia";
import { useTea } from "../lib/tea";
import type { Maybe } from "../lib/maybe";
import {
  initialOutlineActionsModel,
  updateOutlineActions,
  type OutlineHeaderNew,
} from "./useOutlineActionsStore.update";

export type {
  OutlineHeaderNew,
  OutlineHeaderNewItem,
} from "./useOutlineActionsStore.update";

/**
 * Bridges the router-swapped outline pages (OutlineTree/OutlineNotes/
 * OutlineDatabase/OutlineTrash) up to the persistent outline chrome
 * (`EditorOutlinePane.vue`) and the editor shell's native-menu handlers,
 * neither of which can reach the currently-active outline page via a template
 * ref anymore, since it now genuinely mounts/unmounts with the route instead
 * of always being present. Each outline page registers what it can do in
 * `onMounted` and clears it in `onUnmounted`; Storyboard/Trash never register
 * `headerNew`/`cancelCreating` (matching today's `showHeaderNew` exclusion).
 *
 * TEA: pure Model/Msg in `useOutlineActionsStore.update.ts`. The exposed
 * fields are writable computeds so pages keep assigning
 * `outlineActions.headerNew = just(...)`; each write dispatches a Msg.
 */
export const useOutlineActionsStore = defineStore("outlineActions", () => {
  const { model, dispatch } = useTea(
    initialOutlineActionsModel,
    updateOutlineActions,
  );

  /** "New chapter/note/entry" set by whichever of Tree/Notes/Database is
   * mounted; `nothing` on Storyboard/Trash.
   *
   * `items`, when Just, turns the header `+` into a dropdown (the manuscript
   * outline can create acts, chapters, and scenes). `run` stays the default for
   * the app menu's File > New, which has no dropdown to open. */
  const headerNew = computed<Maybe<OutlineHeaderNew>>({
    get: () => model.value.headerNew,
    set: (value) => {
      void dispatch({ type: "SetHeaderNew", value });
    },
  });
  /** Cancels an in-progress create-flow on the active outline page (called
   * before opening Project Settings, mirroring today's behavior). */
  const cancelCreating = computed<Maybe<() => void>>({
    get: () => model.value.cancelCreating,
    set: (value) => {
      void dispatch({ type: "SetCancelCreating", value });
    },
  });
  /** Refreshes the Trash list after a restore; only Trash registers this. */
  const refreshTrash = computed<Maybe<() => void>>({
    get: () => model.value.refreshTrash,
    set: (value) => {
      void dispatch({ type: "SetRefreshTrash", value });
    },
  });

  return {
    headerNew,
    cancelCreating,
    refreshTrash,
  };
});
